import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, ExcelIcon, exportToExcel,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import { C360Table, ColumnChooser, ViewIcon } from './c360';
import { usePageMeta } from '../hooks/usePageMeta';

const API_BASE = '/api/deposit-counts';

// Bu ekran read-only (mobile push ile gelir, admin görüntüler) ve backend OData değil
// REST/MediatR envelope döndürür (ApiResponse<{ items, draftCount, ... }>). Bu yüzden
// useC360List (OData $filter/$orderby) yerine mevcut REST yüklemesi korunup, c360 görsel
// kabuğu (ListPageShell + C360Table + ColumnChooser + Pagination) ve client-side
// filtre/sıralama/sayfalama uygulanır.

const STATUS_TABS = [
  { key: '', label: 'Tümü' },
  { key: 'DRAFT', label: 'Taslak' },
  { key: 'SENT', label: 'Gönderildi' },
  { key: 'PROCESSED', label: 'İşlendi' },
];

const STATUS_META = {
  DRAFT: { label: 'Taslak', tone: 'neutral' },
  SENT: { label: 'Gönderildi', tone: 'wait' },
  PROCESSED: { label: 'İşlendi', tone: 'ok' },
  UNKNOWN: { label: 'Bilinmiyor', tone: 'neutral' },
};

// Mobil DepositType enum'unun Türkçe etiketleri (core/domain/model/DepositCount.kt).
const DEPOSIT_TYPE_LABELS = {
  BOTTLE_GLASS: 'Cam Şişe',
  BOTTLE_PET: 'PET Şişe',
  CRATE: 'Kasa',
  CO2_TANK: 'CO₂ Tankı',
};

const PAGE_SIZE_DEFAULT = 20;

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrapResponse(payload) {
  const data = pick(payload, 'Data', 'data') ?? payload;
  return {
    items: pick(data, 'items', 'Items') ?? [],
    draftCount: pick(data, 'draftCount', 'DraftCount') ?? 0,
    sentCount: pick(data, 'sentCount', 'SentCount') ?? 0,
    processedCount: pick(data, 'processedCount', 'ProcessedCount') ?? 0,
    unitsTotal: pick(data, 'unitsTotal', 'UnitsTotal') ?? 0,
  };
}

function depositTypeLabel(type) {
  if (!type) return '-';
  return DEPOSIT_TYPE_LABELS[type] || type;
}

function statusBadgeFor(status) {
  const meta = STATUS_META[status] || { label: status || '-', tone: 'neutral' };
  return <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>;
}

function formatDate(value) {
  if (!value) return '-';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return '-'; }
}

function toIsoStart(dateStr) {
  if (!dateStr) return '';
  try { return new Date(`${dateStr}T00:00:00`).toISOString(); } catch { return ''; }
}

function toIsoEnd(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(`${dateStr}T00:00:00`);
    d.setDate(d.getDate() + 1);
    return d.toISOString();
  } catch { return ''; }
}

export function DepositCountsPage() {
  usePageMeta('Depozito Sayımları', 'SDM/depoda yapılan geri dönüşlü ambalaj sayımları');

  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchText, setSearchText] = useState('');

  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ draftCount: 0, sentCount: 0, processedCount: 0, unitsTotal: 0 });
  // Yusuf Fix — LİSTE AÇILIŞINDA "Kayıt bulunamadı" YANIP SÖNMESİN: veri mount'taki effect'te
  // çekiliyor, yani ilk boyama fetch'ten ÖNCE olur. loading false başlayınca tablo o ilk karede
  // boş-durum metnini gösteriyor, veri gelince listeye dönüyordu — kullanıcı "önce kayıt yok dedi,
  // sonra liste geldi" diye okuyor. Yükleme durumu ilk kareden itibaren AÇIK başlar.
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [sortField, setSortField] = useState('documentDate');
  const [sortDir, setSortDir] = useState('desc');
  const [selected, setSelected] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});

  const [detailRow, setDetailRow] = useState(null);
  const [detailLines, setDetailLines] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      const isoFrom = toIsoStart(dateFrom);
      const isoTo = toIsoEnd(dateTo);
      if (isoFrom) params.set('from', isoFrom);
      if (isoTo) params.set('to', isoTo);
      params.set('take', '500');

      const res = await fetch(`${API_BASE}?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const payload = await res.json();
      const data = unwrapResponse(payload);
      setRows(Array.isArray(data.items) ? data.items : []);
      setCounts({
        draftCount: data.draftCount,
        sentCount: data.sentCount,
        processedCount: data.processedCount,
        unitsTotal: data.unitsTotal,
      });
      setPage(1);
    } catch (err) {
      showToast('error', err.message || 'Liste alınamadı');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, dateFrom, dateTo, showToast]);

  useEffect(() => { load(); }, [load]);

  const openDetail = useCallback(async (row) => {
    setDetailRow(row);
    setDetailLines(null);
    setDetailLoading(true);
    try {
      const id = pick(row, 'id', 'Id');
      const res = await fetch(`${API_BASE}/${id}/details`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Satırlar alınamadı (${res.status})`);
      const payload = await res.json();
      const data = pick(payload, 'Data', 'data') ?? payload;
      setDetailLines(pick(data, 'items', 'Items') ?? []);
    } catch (err) {
      showToast('error', err.message || 'Satırlar alınamadı');
      setDetailLines([]);
    } finally {
      setDetailLoading(false);
    }
  }, [showToast]);

  const closeDetail = () => { setDetailRow(null); setDetailLines(null); };

  // === client-side filtre + sıralama (backend OData değil) ===
  const filteredSorted = useMemo(() => {
    const needle = searchText.trim().toLocaleLowerCase('tr-TR');
    const match = (row) => {
      if (!needle) return true;
      const fields = [
        pick(row, 'sdmName', 'SdmName'),
        pick(row, 'vendorName', 'VendorName'),
        pick(row, 'shipmentDate', 'ShipmentDate'),
        pick(row, 'notes', 'Notes'),
      ];
      return fields.some((v) => v && String(v).toLocaleLowerCase('tr-TR').includes(needle));
    };
    const filtered = rows.filter(match);
    const dir = sortDir === 'desc' ? -1 : 1;
    const sortKey = (row) => {
      switch (sortField) {
        case 'sdmName': return pick(row, 'sdmName', 'SdmName') || '';
        case 'vendorName': return pick(row, 'vendorName', 'VendorName') || '';
        case 'status': return pick(row, 'status', 'Status') || '';
        case 'totalUnits': return Number(pick(row, 'totalUnits', 'TotalUnits')) || 0;
        case 'detailCount': return Number(pick(row, 'detailCount', 'DetailCount')) || 0;
        case 'shipmentDate': return pick(row, 'shipmentDate', 'ShipmentDate') || '';
        case 'documentDate':
        default: {
          const v = pick(row, 'documentDate', 'DocumentDate');
          return v ? new Date(v).getTime() : 0;
        }
      }
    };
    return [...filtered].sort((a, b) => {
      const av = sortKey(a);
      const bv = sortKey(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [rows, searchText, sortField, sortDir]);

  const totalCount = filteredSorted.length;
  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredSorted.slice(start, start + pageSize);
  }, [filteredSorted, page, pageSize]);

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('documentDate', {
      header: 'Belge Tarihi',
      cell: (info) => formatDate(pick(info.row.original, 'documentDate', 'DocumentDate')),
    }),
    columnHelper.accessor('sdmName', {
      header: 'SDM / Depo',
      cell: (info) => pick(info.row.original, 'sdmName', 'SdmName') || '-',
    }),
    columnHelper.accessor('vendorName', {
      header: 'Distribütör',
      cell: (info) => pick(info.row.original, 'vendorName', 'VendorName') || '-',
    }),
    columnHelper.accessor('shipmentDate', {
      header: 'Sevkiyat',
      cell: (info) => pick(info.row.original, 'shipmentDate', 'ShipmentDate') || '-',
    }),
    columnHelper.accessor('status', {
      header: 'Durum',
      cell: (info) => statusBadgeFor(pick(info.row.original, 'status', 'Status')),
    }),
    columnHelper.accessor('totalUnits', {
      header: 'Toplam Adet',
      cell: (info) => {
        const v = pick(info.row.original, 'totalUnits', 'TotalUnits');
        return (v === null || v === undefined) ? '-' : <span className="c360-erp-chip">{v}</span>;
      },
    }),
    columnHelper.accessor('detailCount', {
      header: 'Satır',
      cell: (info) => pick(info.row.original, 'detailCount', 'DetailCount') ?? 0,
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button
            type="button"
            className="c360-action-btn c360-action-btn--icon c360-action-btn--view"
            title="Detay"
            aria-label="Detay"
            onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}
          ><ViewIcon /></button>
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  const sorting = useMemo(() => (sortField ? [{ id: sortField, desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    setPage(1);
    if (!first) { setSortField('documentDate'); setSortDir('desc'); return; }
    setSortField(first.id);
    setSortDir(first.desc ? 'desc' : 'asc');
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: pagedRows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: page - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(pick(row, 'id', 'Id')),
  });

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { headerName: 'Belge Tarihi', valueGetter: ({ row }) => formatDate(pick(row, 'documentDate', 'DocumentDate')) },
        { headerName: 'SDM / Depo', valueGetter: ({ row }) => pick(row, 'sdmName', 'SdmName') || '' },
        { headerName: 'Distribütör', valueGetter: ({ row }) => pick(row, 'vendorName', 'VendorName') || '' },
        { headerName: 'Sevkiyat', valueGetter: ({ row }) => pick(row, 'shipmentDate', 'ShipmentDate') || '' },
        { headerName: 'Durum', valueGetter: ({ row }) => (STATUS_META[pick(row, 'status', 'Status')]?.label || pick(row, 'status', 'Status') || '') },
        { headerName: 'Toplam Adet', valueGetter: ({ row }) => pick(row, 'totalUnits', 'TotalUnits') ?? '' },
        { headerName: 'Satır', valueGetter: ({ row }) => pick(row, 'detailCount', 'DetailCount') ?? 0 },
      ];
      if (filteredSorted.length === 0) { showToast('error', 'Dışa aktarılacak veri yok'); return; }
      const dateStr = new Date().toISOString().slice(0, 10);
      exportToExcel(filteredSorted, cols, `depozito-sayim_${dateStr}`);
      showToast('success', `${filteredSorted.length} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      <div style={s.toolbarLeft}>
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.key || 'all'}
            type="button"
            onClick={() => setStatusFilter(tab.key)}
            style={{
              ...(statusFilter === tab.key ? s.btnPrimary : s.btnSecondary),
              padding: '6px 14px', fontSize: 13,
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <input
        type="date"
        value={dateFrom}
        onChange={(e) => setDateFrom(e.target.value)}
        style={{ ...s.filterInput, width: 140 }}
        title="Başlangıç"
      />
      <input
        type="date"
        value={dateTo}
        onChange={(e) => setDateTo(e.target.value)}
        style={{ ...s.filterInput, width: 140 }}
        title="Bitiş"
      />
      <input
        type="search"
        value={searchText}
        onChange={(e) => { setSearchText(e.target.value); setPage(1); }}
        placeholder="SDM / distribütör / not..."
        style={s.filterInput}
      />
      {searchText && <button style={s.btnSecondary} onClick={() => { setSearchText(''); setPage(1); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={load} disabled={loading}>{loading ? 'Yükleniyor...' : 'Yenile'}</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={page}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={pagedRows.length}
      onPageChange={setPage}
      onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Depozito Sayımları"
        subtitle="Saha temsilcisinin SDM/depoda yaptığı geri dönüşlü ambalaj sayımları (cam şişe / PET / kasa / CO₂ tankı). Mobile'dan push ile gelir; admin görüntüleme."
        meta={(
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <StatusBadge tone="neutral">Taslak: {counts.draftCount}</StatusBadge>
            <StatusBadge tone="wait">Gönderilen: {counts.sentCount}</StatusBadge>
            <StatusBadge tone="ok">İşlenen: {counts.processedCount}</StatusBadge>
            <StatusBadge tone="neutral">Toplam Adet: {counts.unitsTotal}</StatusBadge>
          </div>
        )}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={openDetail}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {detailRow && (
        <div style={s.overlay} onClick={closeDetail}>
          <div style={{ ...s.modal, maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>
                {pick(detailRow, 'sdmName', 'SdmName') || 'Depozito Sayımı'}
                {pick(detailRow, 'vendorName', 'VendorName') ? ` • ${pick(detailRow, 'vendorName', 'VendorName')}` : ''}
              </h3>
              <button type="button" style={s.closeBtn} onClick={closeDetail}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Durum</label>
                  <div>{statusBadgeFor(pick(detailRow, 'status', 'Status'))}</div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Belge Tarihi</label>
                  <div style={s.inputView}>{formatDate(pick(detailRow, 'documentDate', 'DocumentDate'))}</div>
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Sevkiyat</label>
                  <div style={s.inputView}>{pick(detailRow, 'shipmentDate', 'ShipmentDate') || '-'}</div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Toplam Adet</label>
                  <div style={s.inputView}>{pick(detailRow, 'totalUnits', 'TotalUnits') ?? '-'}</div>
                </div>
              </div>
              {pick(detailRow, 'notes', 'Notes') && (
                <div style={s.formGroup}>
                  <label style={s.label}>Notlar</label>
                  <div style={s.textareaView}>{pick(detailRow, 'notes', 'Notes')}</div>
                </div>
              )}
              <div style={s.formGroup}>
                <label style={s.label}>Kalemler ({detailLines?.length ?? '...'})</label>
                {detailLoading && <div style={{ color: '#6b7280', fontSize: 13 }}>Yükleniyor...</div>}
                {!detailLoading && detailLines && (
                  <div style={{
                    border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden',
                    maxHeight: 320, overflowY: 'auto',
                  }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead style={{ background: '#f9fafb' }}>
                        <tr>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: '#6b7280' }}>Tür</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, fontSize: 11, color: '#6b7280' }}>Adet</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, fontSize: 11, color: '#6b7280' }}>Birim</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: '#6b7280' }}>Not</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailLines.length === 0 && (
                          <tr><td colSpan="4" style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Kalem yok</td></tr>
                        )}
                        {detailLines.map((line, idx) => (
                          <tr key={pick(line, 'id', 'Id') || idx} style={{ borderTop: idx > 0 ? '1px solid #f3f4f6' : 'none' }}>
                            <td style={{ padding: '8px 12px', fontWeight: 600 }}>
                              {depositTypeLabel(pick(line, 'depositType', 'DepositType'))}
                            </td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>
                              {pick(line, 'quantity', 'Quantity') ?? '-'}
                            </td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', color: '#6b7280' }}>
                              {pick(line, 'unit', 'Unit') || '-'}
                            </td>
                            <td style={{ padding: '8px 12px', color: '#6b7280' }}>
                              {pick(line, 'notes', 'Notes') || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
            <div style={s.modalFooter}>
              <button type="button" style={s.btnSecondary} onClick={closeDetail}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default DepositCountsPage;
